export const BRAND = {
  name: "The Intuitive Runner",
  tagline: "Lactate Profiling Studio",
  website: "theintuitiverunner.ca",
  websiteUrl: "https://www.theintuitiverunner.ca",
  logoUrl:
    "https://customer-assets.emergentagent.com/job_lt-test-reports/artifacts/mt3yxxxu_transparent%20logo.png",
};

// Default email template for sending reports. Supports {first_name} and {test_date} placeholders.
// Markdown-style links [label](url) are auto-rendered as clickable hyperlinks in the email.
export const DEFAULT_EMAIL_SUBJECT = "Your Lactate Test Results — {test_date}";
export const DEFAULT_EMAIL_BODY = `Hi {first_name},

Thank you for completing your lactate testing with me! Your full report is attached — take a look at your zones and let me know if you have any questions about your results or training.

If you enjoyed the experience, a Google review means the world to me as a small business. You can [click here](https://search.google.com/local/writereview?placeid=ChIJE3ERbT-gLoIRWO0-bFgP3FY) to leave one.

Happy running,

Natalie Costa
The Intuitive Runner`;

// Compare-report email defaults — used when sending a progress comparison PDF.
export const DEFAULT_COMPARE_EMAIL_SUBJECT = "Your Lactate Progress Report — {test_date}";
export const DEFAULT_COMPARE_EMAIL_BODY = `Hi {first_name},

Attached to this separate email is your lactate progress report — a side-by-side comparison of your previous and most recent tests, showing how your thresholds and training zones have shifted over time. Take a look at the changes in your LT1, LT2, and pace ranges. The numbers tell a great story about your training so far.

Happy running,

Natalie Costa
The Intuitive Runner`;

// Coaching weekly program email — sent with a per-week PDF attached.
export const DEFAULT_COACHING_WEEK_EMAIL_SUBJECT = "Your Weekly Program — Week {week_number}";
export const DEFAULT_COACHING_WEEK_EMAIL_BODY = `Hi {first_name},

Attached is your program for the upcoming week. Take a look through the workouts, zones, and notes — and let me know if anything needs adjusting for your schedule.

Remember: consistency beats intensity, 80% of the plan is still an A, and always listen to your body.

Let's go!

Natalie Costa
The Intuitive Runner`;

// Training zone scaffolding. User fills in ranges manually.
export const DEFAULT_ZONES = [
  { name: "Recovery", zone_label: "Zone 1", hr_range: "", speed_range: "", pace_range: "", color_key: "recovery_z1" },
  { name: "Overdistance / Base", zone_label: "Zone 2", hr_range: "", speed_range: "", pace_range: "", color_key: "zone_2" },
  { name: "Endurance / Tempo", zone_label: "Zone 3", hr_range: "", speed_range: "", pace_range: "", color_key: "zone_3" },
  { name: "Threshold", zone_label: "Zone 4", hr_range: "", speed_range: "", pace_range: "", color_key: "zone_4" },
  { name: "VO2 max", zone_label: "Zone 5", hr_range: "", speed_range: "", pace_range: "", color_key: "zone_5" },
];

export const ZONE_COLORS = {
  recovery_z1: "#DDE6ED",
  zone_2: "#D5E8D4",
  zone_3: "#FFF2CC",
  zone_4: "#FFE6CC",
  zone_5: "#F8CECE",
};

export const ZONE_BORDER_COLORS = {
  recovery_z1: "#A9BECE",
  zone_2: "#92C58A",
  zone_3: "#E6C75A",
  zone_4: "#E2A161",
  zone_5: "#D57A7A",
};

// default number of rows in the warm-up/profile data table (4-min stages up to 52 min like the sample sheet)
export const DEFAULT_TIME_STAGES = [4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52];

export const DEFAULT_COACH_NOTE = `Your training HR zones were identified based on your body's response to exercise during your lactate test. Based on your lactate response, focus on accumulating time in your new Zone 2 to "flatten" the curve, and layer in speed intervals above LT2 to shift the steep part of the curve further to the right. As your fitness improves, your HR zones will shift. It's important to re-establish your zones periodically to ensure your training is providing the desired stimulus.

Your HR is influenced by many factors, including temperature, humidity, fatigue, body hydration, cardiovascular drift, stress, etc. It's natural for your speed/pace at a given HR to vary day-to-day. HR training accounts for these variations to slow you down on days when your body needs more rest, and encourages you to push the pace when the conditions are right. Your heart doesn't know speed or pace, it only understands effort.`;

// Page 3 zone guide. Combines physiological benefits ("what it does to you")
// with practical training usage ("how to use it") for each of the 5 zones.
// Used as the per-test default; coaches can edit any of these strings on a test.
export const ZONE_BENEFITS = [
  {
    label: "Zone 1 · Recovery",
    points: [
      "Warm-up zone",
      "Cool-down zone",
      "Active rest or recovery during interval training",
    ],
    howTo:
      "Use during warm-ups, cool-downs, and the rest periods between speed or hill intervals. Stay here on active-recovery days or when you're feeling sluggish or extra fatigued. Easy / recovery runs can also live here.",
  },
  {
    label: "Zone 2 · Base-Building / Easy Run",
    points: [
      "Improves basic endurance, aerobic capacity & efficiency",
      "Increases mitochondrial density / biogenesis",
      "Increases capillarization",
      "Increased fat oxidation",
      "Transforms Type IIa/b muscle fibres to Type I",
    ],
    howTo:
      "Your long, slower runs and easy runs. This is the lower half of your HR range between LT1 and LT2. Spend more time here. The more volume you accumulate, the more the curve flattens.",
  },
  {
    label: 'Zone 3 · Tempo / Endurance ("Happy Hard")',
    points: [
      "Lactate threshold sits at the high end of this zone",
      "Increased mitochondrial density and enzymes",
      "Improved capillarization",
    ],
    howTo:
      "A step up from base-building, higher-intensity aerobic work. The upper half of your HR range between LT1 and LT2. Save this for prescribed tempo / race-pace runs.",
  },
  {
    label: "Zone 4 · Lactate Threshold (LT)",
    points: [
      "Lactate by-product accumulates right above threshold",
      "More glycogen / carb use than fat",
      "Increased lactate threshold & buffering capacity",
      "Increase in stroke volume, cardiac output & plasma volume",
    ],
    howTo:
      "This zone is reserved for tempo and speed sessions designed to improve your metabolic efficiency. For tempo intervals, you can weave between Zone 4 work bouts and Zone 3 recovery. For speed intervals (of RPE 7-8), maintain Zone 4 during the effort and allow your heart rate to drop back to Zone 1 during rest periods to improve cardiovascular recovery. Training consistently at or slightly above your LT2 creates a physiological stress that increases your threshold, allowing you to run faster at lower efforts over time.",
  },
  {
    label: "Zone 5 · Maximal / VO2 Max",
    points: [
      "VO2 Max training / all-out sprints",
      "Increase in stroke volume, cardiac output & plasma volume",
      "Improved lactate buffering capacity",
      "Muscular power development",
    ],
    howTo:
      "Sprints and high-RPE work (think speed / hill runs at RPE 9-10), with HR above LT2 and pushing toward your max.",
  },
];
